import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IClaimAutocompleteResultDto, IClaimConditionNodeDto, ILogicTypeEnum } from '../../../../interfaces/IUtilities';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ClaimTypeahead from './ClaimTypeahead';
import { LogicTypeEnumText } from '../../../../helpers/EnumeratorHelper';
import { getAllClaims } from '../../../../helpers/ClaimBuilderHelper';
import { useTranslation } from 'react-i18next';
import { ProfileType } from '../../../../constants/constants';
import BaseSelect from '../Select/BaseSelect';

export interface ClaimNodeEditorHandle {
  forceShowError: () => void;
  reset: () => void;
}

interface NodeEditorProps {
  node: IClaimConditionNodeDto;
  onChange: (node: IClaimConditionNodeDto) => void;
  onRemove: () => void;
  validationErrors?: Record<string, string>;
  registerRef: (id: string, ref: ClaimNodeEditorHandle | null) => void;
  onClaimMapUpdate?: (map: Record<string, string>) => void;
  isEdit: boolean
}

const ClaimNodeEditor = forwardRef<ClaimNodeEditorHandle, NodeEditorProps>(
  ({ node, onChange, onRemove, validationErrors, registerRef, onClaimMapUpdate, isEdit }, ref) => {
    const { t } = useTranslation();
    const isGroup = node.logicType !== ILogicTypeEnum.LEAF;
    const claimTypeaheadRef = useRef<{ clear: () => void }>(null);
    const [customValueStr, setCustomValueStr] = useState('');
    const [savedClaimValue, setSavedClaimValue] = useState('');
    const [useCustomValue, setUseCustomValue] = useState(false);
    const [claimName, setClaimName] = useState('');
    const [removeRootVisible, setRemoveRootVisible] = useState(false);
    const [profileTypeValue, setProfileTypeValue] = useState('');

    const formik = useFormik({
      initialValues: { claimId: node.claimId || '' }, //Set profile type Required: initialValues: initialValues: { claimId: node.claimId || '', ptValue: node.customValue || '' },
      enableReinitialize: true,
      validationSchema: Yup.object({
        claimId: Yup.string().trim().required(t('FieldRequired', { field: t('Claim') })) //,
        // ptValue: Yup.string().when([], {
        //   is: () => claimName.toLocaleLowerCase().includes(ProfileType),
        //   then: (schema) => schema.required(t('FieldRequired', { field: t('ProfileType') })),
        //   otherwise: (schema) => schema.notRequired()}) //Set profile type Required
      }),
      onSubmit: () => {},
    });

    useImperativeHandle(ref, () => ({
      forceShowError: () => {
        formik.setTouched({ claimId: true }, true);
        formik.validateForm();
      },
      reset: () => {
        formik.setFieldValue('claimId', node.claimId ?? '');
        formik.setTouched({ claimId: false });
        // formik.setFieldValue('ptValue', node.customValue ?? ''); //Set profile type Required
        // formik.setTouched({ ptValue: false });
        setCustomValueStr('');
        setUseCustomValue(false);
        setSavedClaimValue('');
        setClaimName('');
        claimTypeaheadRef.current?.clear?.();
      }
    }));

    useEffect(() => {
      registerRef(node.id, {
          forceShowError: () => {
            formik.setTouched({ claimId: true });
            // if (claimName.toLocaleLowerCase().includes(ProfileType)) {
            //   formik.setTouched({ ptValue: true });
            // } //Set profile type Required
          },
      reset: () => {
          formik.setValues({ claimId: '' }); //Set profile type Required: formik.setValues({ claimId: '', ptValue: '' });
          formik.setTouched({ claimId: false });
          //formik.setTouched({ ptValue: false }); //Set profile type Required
          setCustomValueStr('');
          setUseCustomValue(false);
          setSavedClaimValue('');
          setClaimName('');
          onChange({ ...node, claimId: null, customValue: null });
          claimTypeaheadRef.current?.clear();
        }
      }); 

      const fetchClaimValue = async () => {
        const isUsingCustom = Boolean(node.customValue === null ? false : true);
        setUseCustomValue(isUsingCustom);
        setCustomValueStr(node.customValue ?? '');
        setProfileTypeValue(node.customValue ?? '');

        const allClaims = await getAllClaims();
        let found: IClaimAutocompleteResultDto | undefined;
        if (node.claimId !== null) {
          found = allClaims.find(c => c.id === node.claimId);
          setClaimName(found?.type.toLocaleLowerCase().includes(ProfileType) ? found?.type : '');
        } 
       
        if (!isUsingCustom && node.claimId) {
          const fetchedVal = found?.value ?? '';
          setSavedClaimValue(fetchedVal);
        }
      };

      fetchClaimValue();
  
      if (node.parentId === null && node.children.length === 1) {
        setRemoveRootVisible(true);
      } else {
        setRemoveRootVisible(false);
      }

      return () => {
          registerRef(node.id, null);
      };
    }, [node.id, formik, registerRef, isEdit, node.customValue]);

    const errorMessage = validationErrors?.[`${node.id}.claimId`] || formik.errors.claimId;
    const showError = Boolean(errorMessage);

    const updateChild = (index: number, updated: IClaimConditionNodeDto) => {
      const updatedChildren = [...node.children];
      updatedChildren[index] = updated;
      onChange({ ...node, children: updatedChildren });
    };

    const addLeaf = () => {
      const newLeaf: IClaimConditionNodeDto = {
        id: uuidv4(),
        transactionId: node.transactionId,
        logicType: ILogicTypeEnum.LEAF,
        claimId: '',
        parentId: node.id,
        orderIndex: node.children.length + 1,
        negated: false,
        customValue: null,
        children: []
      };
      onChange({ ...node, children: [...node.children, newLeaf] });
    };

    const addGroup = (logicType: ILogicTypeEnum.AND | ILogicTypeEnum.OR | ILogicTypeEnum.ANDNOT | ILogicTypeEnum.ORNOT) => {
        const newGroup: IClaimConditionNodeDto = {
            id: uuidv4(),
            transactionId: node.transactionId,
            logicType,
            parentId: node.id,
            orderIndex: node.children.length + 1, 
            negated: false,
            customValue: null,
            children: []
        };
        onChange({ ...node, children: [...node.children, newGroup] });
    };

    const removeChild = (index: number) => {
      const children = [...node.children];
      children.splice(index, 1);
      onChange({ ...node, children });
    };

    const addRoot = () => {
      var newRootId = uuidv4();
      const newNode: IClaimConditionNodeDto = {
        id: node.id,
        transactionId: node.transactionId,
        logicType: node.logicType,
        claimId: null,
        parentId: newRootId,
        orderIndex: 1,
        negated: false,
        customValue: null,
        children: node.children
      };

      onChange({ ...node, id: newRootId, logicType: ILogicTypeEnum.AND, parentId: null, orderIndex: 1, children: [newNode] });
      setRemoveRootVisible(true);
    };

    const removeRoot = () => {
      if (!node.children || node.children.length !== 1) {
        console.log(t('CannotRemoveRoot'));
        return;
      }

      const onlyChild = node.children[0];
      onChange({ ...onlyChild, parentId: null, orderIndex: 1 });
      setRemoveRootVisible(false);
    };

    const logicTypeOptions = [
      { value: ILogicTypeEnum.AND, label: LogicTypeEnumText[ILogicTypeEnum.AND] },
      { value: ILogicTypeEnum.OR, label: LogicTypeEnumText[ILogicTypeEnum.OR] },
      { value: ILogicTypeEnum.ANDNOT, label: LogicTypeEnumText[ILogicTypeEnum.ANDNOT] },
      { value: ILogicTypeEnum.ORNOT, label: LogicTypeEnumText[ILogicTypeEnum.ORNOT] },
    ];

    const profileTypeOptions = [
      { value: 'Individual', label: t('Individual') },
      { value: 'Organisation', label: t('Organisation') },
      { value: 'Agent', label: t('Agent') },
      { value: 'Spoke', label: t('Spoke') },
    ];

    if (isGroup) {
      return (
        <div className="border rounded p-3 my-2">
          <div className="d-flex align-items-center mb-2">
            <label className="me-2">{t('LogicType')}</label>
            <div style={{ width: 220 }}>
              <BaseSelect
                options={logicTypeOptions}
                value={logicTypeOptions.find(o => o.value === node.logicType) ?? null}
                onChange={(option: any) => onChange({ ...node, logicType: option?.value ?? ILogicTypeEnum.AND })}
              />
            </div>
            {node.parentId === null && (
              <button onClick={addRoot} className="btn gg-btn-black ms-auto">{t('AddRoοt')}</button>
            )}
          </div>

          {node.children.map((child, idx) => (
            <ClaimNodeEditor
              key={child.id}
              node={child}
              onChange={(updated) => updateChild(idx, updated)}
              onRemove={() => removeChild(idx)}
              validationErrors={validationErrors}
              registerRef={registerRef}
              isEdit={isEdit}
            />
          ))}

          <div className="d-flex gap-2 mt-2">
            <button onClick={addLeaf} className="btn gg-btn-submit">+ {t('Claim')}</button>
            <button onClick={() => addGroup(ILogicTypeEnum.AND)} className="btn gg-btn">+ {t('Group')} ({LogicTypeEnumText[ILogicTypeEnum.AND]})</button>
            <button onClick={() => addGroup(ILogicTypeEnum.OR)} className="btn gg-btn">+ {t('Group')} ({LogicTypeEnumText[ILogicTypeEnum.OR]})</button>
            <button onClick={() => addGroup(ILogicTypeEnum.ANDNOT)} className="btn gg-btn">+ {t('Group')} ({LogicTypeEnumText[ILogicTypeEnum.ANDNOT]})</button>
            <button onClick={() => addGroup(ILogicTypeEnum.ORNOT)} className="btn gg-btn">+ {t('Group')} ({LogicTypeEnumText[ILogicTypeEnum.ORNOT]})</button>
            <button onClick={onRemove} className="btn btn-outline-danger">{t('RemoveGroup')}</button>
            {(node.parentId === null && removeRootVisible) && (
              <button onClick={removeRoot} className="btn btn-outline-danger ms-auto">{t('RemoveRoοt')}</button>
            )}
          </div>
        </div>
      );
    }


    return (
      <>
        <div className="row mb-2">
          {/* <input
            name="claimId"
            className={`form-control ${showError ? 'is-invalid' : ''}`}
            placeholder="Claim ID"
            value={formik.values.claimId}
            onChange={formik.handleChange}
            onBlur={handleChange}
          /> Old code with Input */}

          <div className="col">
            <input
              type="checkbox"
              className="form-check-input"
              id={`negate-${node.id}`}
              checked={node.negated || false}
              onChange={() => onChange({ ...node, negated: !node.negated })}
            />
            <label className="form-check-label ps-2" htmlFor={`negate-${node.id}`}>
              {t('NegateCondition')}
            </label>

            <ClaimTypeahead
              id={`claim-${node.id}`}
              defaultValue={formik.values.claimId}
              showError={showError}
              formik={formik}
              ref={claimTypeaheadRef}
              onClaimChanged={(claim) => {
                const claimId = claim?.id ?? '';
                const claimValue = claim?.value ?? '';
                const claimName = claim?.type ?? '';
                formik.setFieldValue('claimId', claimId);
                //formik.setFieldValue('ptValue', ''); //Set profile type Required

                setSavedClaimValue(claimValue);
                setCustomValueStr(claimValue);
                setUseCustomValue(false);
                setClaimName(claimName);

                if (useCustomValue) {
                  onChange({ ...node, claimId, customValue: claimValue });
                } else {
                  onChange({ ...node, claimId, customValue: null });
                }
              }}
              onClaimMapUpdate={onClaimMapUpdate}
            />

            {formik.touched.claimId && showError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>

          <div className="col">
            <input
              type="checkbox"
              className="form-check-input"
              id={`custom-toggle-${node.id}`}
              checked={useCustomValue && !(claimName.toLocaleLowerCase().includes(ProfileType))}
              disabled={(claimName.toLocaleLowerCase().includes(ProfileType))}
              onChange={(e) => {
                const checked = e.target.checked;

                if (!node.claimId) return;
                
                setUseCustomValue(checked);

                if (checked) {
                  const newVal = customValueStr || savedClaimValue || '';          
                  setCustomValueStr(newVal);
                  onChange({ ...node, customValue: newVal });
                } else {
                  setCustomValueStr(savedClaimValue);
                  onChange({ ...node, customValue: null });
                } 
              }}
            />
            <label className="form-check-label ps-2" htmlFor={`custom-toggle-${node.id}`}>
              {t('SetCustomValue')}
            </label>

            {(useCustomValue && !(claimName.toLocaleLowerCase().includes(ProfileType))) ? 
              (<input
                type="text"
                className="form-control"
                value={customValueStr}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomValueStr(val);
                  
                  onChange({ ...node, customValue: val });              
                }}
                placeholder={t("SetClaimValue")}
              />)
              : (claimName.toLocaleLowerCase().includes(ProfileType)) ? 
              (<>
                {/* Set profile type Required
                <select
                  className={`form-select ${formik.touched.ptValue && formik.errors.ptValue ? 'is-invalid' : ''}`}
                  value={formik.values.ptValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    
                    formik.setFieldValue('ptValue', val);

                    setCustomValueStr(val);
                    onChange({ ...node, customValue: val });
                  }}
                >
                  <option value="" disabled>-- {t('Select')} --</option>
                  <option value="Individual">{t('Individual')}</option>
                  <option value="Organisation">{t('Organisation')}</option>
                  <option value="Agent">{t('Agent')}</option>
                  <option value="Spoke">{t('Spoke')}</option>
                </select>
                {formik.touched.ptValue && formik.errors.ptValue && (
                  <div className="invalid-feedback d-block">{formik.errors.ptValue}</div>
                )} */}
                <BaseSelect
                  options={profileTypeOptions}
                  isClearable
                  value={profileTypeOptions.find(o => o.value === profileTypeValue) ?? null}
                  onChange={(option: any) => {
                    const val = option?.value ?? '';
                    setCustomValueStr(val);
                    setProfileTypeValue(val);
                    onChange({ ...node, customValue: val === "" ? null : val });
                  }}
                />
              </>) 
              : 
              <input
                type="text"
                className="form-control"
                value={savedClaimValue}
                disabled /> 
            }
          </div> 

          <div className="col-auto text-end mt-4">
            <button
              className="btn btn-outline-danger"
              onClick={() => {
                const isOnlyRootNode = !node.parentId;
                if (isOnlyRootNode) {
                  formik.setFieldValue('claimId', '');
                  formik.setTouched({ claimId: true });
                  //formik.setFieldValue('ptValue', ''); //Set profile type Required
                  //formik.setTouched({ ptValue: true });
                  setCustomValueStr('');
                  setUseCustomValue(false);
                  setSavedClaimValue('');
                  setClaimName('');
                  onChange({ ...node, claimId: null, customValue: null });
                  claimTypeaheadRef.current?.clear();
                } else {
                  onRemove();
                }
              }}>
              {t('Remove')}
            </button>
          </div>
            
        </div>
      </>
    );
  }
);

export default ClaimNodeEditor;
